'use client';

/**
 * Everything behind this gate can assume a profile exists.
 *
 * It also owns the hydration boundary: saved progress lives in localStorage,
 * which does not exist on the server, so the first client render must match the
 * server's. The gate renders a stable skeleton until `hydrated` flips, which
 * is both correct and — because the skeleton is the same height as the real
 * content — free of layout shift.
 *
 * Creating a "profile" here means typing a first name. No email, no password,
 * no account: children's data should not require collecting more of it.
 */
import React, { useState } from 'react';
import { useKids } from '../context/KidsProvider';
import Avatar from './Avatar';
import { LoadingBlock } from './ui';
import { DEFAULT_AVATAR } from '../lib/progress';
import type { KidProfile } from '../types';

const AGE_BANDS: { id: KidProfile['ageBand']; label: string; emoji: string }[] = [
  { id: '5-7', label: '5–7', emoji: '🐣' },
  { id: '8-10', label: '8–10', emoji: '🦊' },
  { id: '11-14', label: '11–14', emoji: '🚀' },
  { id: 'unspecified', label: 'Rather not say', emoji: '🙂' },
];

export default function ProfileGate({ children }: { children: React.ReactNode }) {
  const { hydrated, store, profile, createProfile, switchProfile } = useKids();
  const [name, setName] = useState('');
  const [ageBand, setAgeBand] = useState<KidProfile['ageBand']>('8-10');
  const [creating, setCreating] = useState(false);

  if (!hydrated) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-10">
        <LoadingBlock height={320} />
      </div>
    );
  }

  if (profile) return <>{children}</>;

  const hasOthers = store.profiles.length > 0;

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="k-card-lg p-6 sm:p-8 k-anim-rise">
        <div className="text-center">
          <div style={{ fontSize: 52 }} aria-hidden="true">
            👋
          </div>
          <h1 className="mt-3" style={{ fontSize: 'clamp(1.5rem,1.2rem+1.4vw,2rem)' }}>
            {hasOthers ? 'Who is typing?' : 'Welcome! What should we call you?'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {hasOthers
              ? 'Pick a typist, or add a new one.'
              : 'Just a first name — nothing else. Your progress is saved on this device only.'}
          </p>
        </div>

        {hasOthers && !creating && (
          <>
            <ul className="mt-6 grid gap-2">
              {store.profiles.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => switchProfile(p.id)}
                    className="k-card-interactive w-full flex items-center gap-3 p-3 text-left"
                  >
                    <Avatar config={p.avatar} size={44} />
                    <span className="flex-1 min-w-0">
                      <span className="block font-extrabold truncate">{p.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {p.xp.toLocaleString()} XP · {p.streak.current} day streak
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="k-btn k-btn-secondary w-full mt-4"
            >
              + Add another typist
            </button>
          </>
        )}

        {(!hasOthers || creating) && (
          <form
            className="mt-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              createProfile(name, ageBand);
            }}
          >
            <div className="flex justify-center mb-5">
              <Avatar config={DEFAULT_AVATAR} size={84} animated />
            </div>

            <label htmlFor="kid-name" className="k-eyebrow block mb-2">
              First name
            </label>
            <input
              id="kid-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              autoComplete="off"
              placeholder="e.g. Zara"
              required
              className="w-full px-4 text-lg font-bold rounded-2xl outline-none"
              style={{
                height: 56,
                background: 'var(--input)',
                border: '2px solid var(--border-strong)',
                color: 'var(--foreground)',
              }}
            />

            <fieldset className="mt-5">
              <legend className="k-eyebrow mb-2">How old are you?</legend>
              <p className="text-xs text-muted-foreground mb-2">
                Only used to show a fair speed comparison. You can skip it.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {AGE_BANDS.map((band) => (
                  <button
                    key={band.id}
                    type="button"
                    onClick={() => setAgeBand(band.id)}
                    aria-pressed={ageBand === band.id}
                    className="k-btn k-btn-secondary k-btn-sm justify-start"
                    style={
                      ageBand === band.id
                        ? { borderColor: 'var(--accent)', color: 'var(--accent)' }
                        : undefined
                    }
                  >
                    <span aria-hidden="true">{band.emoji}</span>
                    {band.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <button
              type="submit"
              className="k-btn k-btn-primary w-full mt-6"
              disabled={!name.trim()}
            >
              Start my journey 🚀
            </button>

            {hasOthers && (
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="k-btn k-btn-ghost w-full mt-2"
              >
                Cancel
              </button>
            )}
          </form>
        )}

        <p className="text-xs text-muted-foreground text-center mt-6">
          No account, no email, no tracking. Everything stays in this browser and can be deleted at
          any time from the grown-ups page.
        </p>
      </div>
    </div>
  );
}
